// src/components/Library/NoteCard.js
import React, { useState, useRef, useEffect, useCallback } from "react";
import { normalizeColorKey, checkEnabled, formatDate } from "../utils/libraryUtils";

const COLOR_VARIANTS = {
  blue: {
    bar: "bg-blue-500",
    topAccent: "bg-blue-500",
    border: "border-blue-500",
    iconBg: "bg-blue-100",
    iconText: "text-blue-600",
    btn: "text-blue-600 bg-blue-50/80 hover:bg-blue-100 border border-blue-100",
    badge: "text-blue-700 bg-blue-50 border-blue-200/80",
    glow: "bg-blue-50",
    dot: "bg-blue-500",
  },
  orange: {
    bar: "bg-orange-500",
    topAccent: "bg-orange-500",
    border: "border-orange-500",
    iconBg: "bg-orange-100",
    iconText: "text-orange-600",
    btn: "text-orange-600 bg-orange-50/80 hover:bg-orange-100 border border-orange-100",
    badge: "text-orange-700 bg-orange-50 border-orange-200/80",
    glow: "bg-orange-50",
    dot: "bg-orange-500",
  },
  purple: {
    bar: "bg-purple-500",
    topAccent: "bg-purple-500",
    border: "border-purple-500",
    iconBg: "bg-purple-100",
    iconText: "text-purple-600",
    btn: "text-purple-600 bg-purple-50/80 hover:bg-purple-100 border border-purple-100",
    badge: "text-purple-700 bg-purple-50 border-purple-200/80",
    glow: "bg-purple-50",
    dot: "bg-purple-500",
  },
  emerald: {
    bar: "bg-emerald-500",
    topAccent: "bg-emerald-500",
    border: "border-emerald-500",
    iconBg: "bg-emerald-100",
    iconText: "text-emerald-600",
    btn: "text-emerald-600 bg-emerald-50/80 hover:bg-emerald-100 border border-emerald-100",
    badge: "text-emerald-700 bg-emerald-50 border-emerald-200/80",
    glow: "bg-emerald-50",
    dot: "bg-emerald-500",
  },
  red: {
    bar: "bg-red-500",
    topAccent: "bg-red-500",
    border: "border-rose-500",
    iconBg: "bg-red-100",
    iconText: "text-red-600",
    btn: "text-red-600 bg-red-50/80 hover:bg-red-100 border border-red-100",
    badge: "text-red-700 bg-red-50 border-red-200/80",
    glow: "bg-red-50",
    dot: "bg-red-500",
  },
  gray: {
    bar: "bg-slate-500",
    topAccent: "bg-slate-500",
    border: "border-slate-400",
    iconBg: "bg-slate-100",
    iconText: "text-slate-600",
    btn: "text-slate-600 bg-slate-50/80 hover:bg-slate-100 border border-slate-200",
    badge: "text-slate-700 bg-slate-50 border-slate-200/80",
    glow: "bg-slate-50",
    dot: "bg-slate-500",
  },
};

const NoteCard = ({
  note,
  folderName,
  onStudy,
  onRename,
  onColorChange,
  onMove,
  onDelete,
  onDownloadPdf,
  onShare,
  onArchive,
  onUnarchive,
  showVerified = true,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [tempTitle, setTempTitle] = useState(note.title || note.topic || "Untitled");

  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const theme = COLOR_VARIANTS[normalizeColorKey(note.color)] || COLOR_VARIANTS.blue;
  const sharedBadgeClass = theme.badge;
  const sharedAccessLabel = note.sharedFrom?.code ? "CODE" : "SHARED";
  const config = note.config || {};

  const showSummary = checkEnabled(config.summaryEnabled);
  const showFlashcards = checkEnabled(config.flashcardsEnabled);
  const showQuiz = checkEnabled(config.quizEnabled);

  const activeCount = (showSummary ? 1 : 0) + (showFlashcards ? 1 : 0) + (showQuiz ? 1 : 0);
  const cols = activeCount === 3 ? "grid-cols-3" : activeCount === 2 ? "grid-cols-2" : "grid-cols-1";
  const anyEnabled = activeCount > 0;

  const submitRename = useCallback(() => {
    setIsRenaming(false);
    const cleanTitle = tempTitle.trim().slice(0, 30);
    if (cleanTitle !== "" && cleanTitle !== (note.title || note.topic)) {
      if (onRename) onRename(note, cleanTitle);
    } else {
      setTempTitle(note.title || note.topic || "Untitled");
    }
  }, [tempTitle, note, onRename]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
      if (isRenaming && inputRef.current && !inputRef.current.contains(event.target)) {
        submitRename();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isRenaming, submitRename]);

  useEffect(() => {
    if (isRenaming && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isRenaming]);

  const handleAction = (e, mode) => {
    e.stopPropagation();
    if (onStudy) onStudy(note, mode);
  };

  const handleMenuClick = (e) => {
    e.stopPropagation();
    setShowMenu(!showMenu);
  };

  const handleStartRename = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    setTempTitle((note.title || note.topic || "Untitled").slice(0, 30));
    setIsRenaming(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      submitRename();
    } else if (e.key === "Escape") {
      setIsRenaming(false);
      setTempTitle(note.title || note.topic || "Untitled");
    }
  };

  const handleMove = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onMove) onMove(note);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDelete) onDelete(note);
  };

  const handleDownloadPdf = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onDownloadPdf) onDownloadPdf(note);
  };

  const handleColorSelect = (e, colorKey) => {
    e.stopPropagation();
    if (onColorChange) onColorChange(note, colorKey);
  };

  const handleShare = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onShare) onShare(note);
  };

  const handleArchive = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onArchive) onArchive(note);
  };

  const handleUnarchive = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    if (onUnarchive) onUnarchive(note);
  };

  return (
    <article
      onClick={() => !isRenaming && anyEnabled && onStudy && onStudy(note)}
      className={`group relative bg-white border ${theme.border} rounded-3xl flex flex-col justify-between shadow-sm hover:shadow-xl transition-all duration-300 border-t-4 hover:-translate-y-1 p-5 ${
        showMenu ? "z-30 overflow-visible" : "z-10 overflow-hidden"
      } ${
        anyEnabled ? "cursor-pointer" : "cursor-default opacity-80"
      }`}
    >
      {/* Soft ambient corner decorative glow */}
      <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
        <div
          className={`absolute -right-6 -top-6 w-24 h-24 ${theme.glow} rounded-full transition-transform duration-500 group-hover:scale-125 opacity-60`}
        />
      </div>

      <div className={`relative ${showMenu ? "z-40" : "z-20"}`}>
        {/* Header Section: Icon, Badges & Burger Menu */}
        <div className="flex justify-between items-start mb-3.5">
          <div
            className={`w-10 h-10 rounded-2xl ${theme.iconBg} ${theme.iconText} flex items-center justify-center text-lg shadow-xs transition-transform group-hover:scale-105 duration-200`}
          >
            <i className="fa-solid fa-file-lines"></i>
          </div>

          <div className="flex items-center gap-1.5">
            {note.isShared && (
              <span
                className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md border flex items-center gap-1 uppercase tracking-wider ${sharedBadgeClass}`}
                title="Shared Reviewer"
              >
                <i
                  className={`fa-solid ${
                    note.sharedFrom?.code ? "fa-globe text-[9px]" : "fa-user-lock text-[9px]"
                  }`}
                ></i>
                <span>{sharedAccessLabel}</span>
              </span>
            )}

            {showVerified && note.verified && (
              <span
                className="text-[10px] font-extrabold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/80 flex items-center gap-1 uppercase tracking-wider shadow-xs"
                title="Verified on Blockchain"
              >
                <i className="fa-solid fa-circle-check text-[9px] text-emerald-600"></i>
                <span>Verified</span>
              </span>
            )}

            {/* 3-Dot Dropdown Menu */}
            <div className="relative" ref={menuRef}>
              <button
                type="button"
                onClick={handleMenuClick}
                className={`w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition ${
                  showMenu ? "bg-gray-100 text-gray-700" : ""
                }`}
                title="More options"
              >
                <i className="fa-solid fa-ellipsis-vertical text-xs"></i>
              </button>

              {showMenu && (
                <div
                  className="absolute right-0 top-8 w-48 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-150 origin-top-right p-1.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col gap-0.5">
                    <button
                      type="button"
                      onClick={handleStartRename}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-xl flex items-center gap-2.5 transition"
                    >
                      <i className="fa-solid fa-pen text-blue-500 text-xs w-4 text-center"></i>
                      <span>Rename</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleMove}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-amber-50 hover:text-amber-600 rounded-xl flex items-center gap-2.5 transition"
                    >
                      <i className="fa-solid fa-folder-open text-amber-500 text-xs w-4 text-center"></i>
                      <span>Move to Folder</span>
                    </button>

                    <button
                      type="button"
                      onClick={handleDownloadPdf}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-rose-50 hover:text-rose-600 rounded-xl flex items-center gap-2.5 transition"
                    >
                      <i className="fa-solid fa-file-pdf text-rose-500 text-xs w-4 text-center"></i>
                      <span>Download PDF</span>
                    </button>

                    {!note.isShared && onShare && (
                      <button
                        type="button"
                        onClick={handleShare}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-indigo-50 hover:text-indigo-600 rounded-xl flex items-center gap-2.5 transition"
                      >
                        <i className="fa-solid fa-share-nodes text-indigo-500 text-xs w-4 text-center"></i>
                        <span>Share Reviewer</span>
                      </button>
                    )}

                    {onArchive && (
                      <button
                        type="button"
                        onClick={handleArchive}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-sky-600 hover:bg-sky-50 rounded-xl flex items-center gap-2.5 transition"
                      >
                        <i className="fa-solid fa-box-archive text-sky-500 text-xs w-4 text-center"></i>
                        <span>Archive</span>
                      </button>
                    )}

                    {onUnarchive && (
                      <button
                        type="button"
                        onClick={handleUnarchive}
                        className="w-full text-left px-3 py-2 text-xs font-semibold text-sky-600 hover:bg-sky-50 rounded-xl flex items-center gap-2.5 transition"
                      >
                        <i className="fa-solid fa-box-open text-sky-500 text-xs w-4 text-center"></i>
                        <span>Unarchive</span>
                      </button>
                    )}

                    <div className="border-t border-gray-100 my-1" />

                    <button
                      type="button"
                      onClick={handleDelete}
                      className="w-full text-left px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-2.5 transition"
                    >
                      <i className="fa-solid fa-trash text-red-500 text-xs w-4 text-center"></i>
                      <span>Delete</span>
                    </button>
                  </div>

                  {/* Color Picker Swatches */}
                  <div className="border-t border-gray-100 my-1" />
                  <div className="p-2">
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2 px-1">
                      Card Theme
                    </p>
                    <div className="grid grid-cols-6 gap-1.5 px-0.5">
                      {Object.keys(COLOR_VARIANTS).map((colorKey) => {
                        const isCurrent = normalizeColorKey(note.color) === colorKey;
                        return (
                          <button
                            key={colorKey}
                            type="button"
                            onClick={(e) => handleColorSelect(e, colorKey)}
                            className={`w-6 h-6 rounded-full ${
                              COLOR_VARIANTS[colorKey].dot
                            } hover:scale-110 transition-all flex items-center justify-center ${
                              isCurrent
                                ? "ring-2 ring-offset-2 ring-gray-400 scale-105 shadow-xs"
                                : "opacity-80 hover:opacity-100"
                            }`}
                            title={colorKey.toUpperCase()}
                          >
                            {isCurrent && (
                              <i className="fa-solid fa-check text-[9px] text-white"></i>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Title Area */}
        {isRenaming ? (
          <div onClick={(e) => e.stopPropagation()} className="mb-2">
            <input
              ref={inputRef}
              type="text"
              value={tempTitle}
              onChange={(e) => setTempTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              maxLength={30}
              className="w-full text-base font-bold text-gray-900 border-b-2 border-blue-600 focus:outline-none bg-blue-50/50 px-1 py-0.5 rounded-t"
            />
            <div className="flex items-center justify-between mt-1 text-[10px] text-gray-400">
              <div className="flex items-center gap-1.5">
                <span>Enter to save</span>
                <span>•</span>
                <span>Esc to cancel</span>
              </div>
              <span className="font-semibold text-gray-400">{tempTitle.length}/30</span>
            </div>
          </div>
        ) : (
          <h3
            className="font-bold text-gray-900 mb-1.5 line-clamp-2 text-base leading-snug tracking-tight group-hover:text-blue-600 transition-colors"
            title={note.title || note.topic || "Untitled"}
          >
            {note.title || note.topic || "Untitled"}
          </h3>
        )}

        {/* Folder & Date Metadata */}
        <p className="text-[11px] text-gray-400 font-medium flex items-center gap-1.5 mb-4 truncate">
          <i className="fa-regular fa-folder text-[10px]"></i>
          <span className="truncate max-w-[120px]">{folderName || "Unorganized"}</span>
          <span>•</span>
          <i className="fa-regular fa-calendar text-[10px]"></i>
          <span className="truncate">{formatDate(note.date || note.createdAt)}</span>
        </p>
      </div>

      {/* Action Footer: Summary, Cards, Quiz */}
      <div className="relative z-0 pt-3 border-t border-gray-100">
        <div className={`grid ${cols} gap-2`}>
          {showSummary && (
            <button
              type="button"
              onClick={(e) => handleAction(e, "summary")}
              className="flex flex-col items-center justify-center py-2.5 rounded-xl text-[10px] font-bold text-gray-600 bg-gray-50 hover:bg-gray-100 hover:text-gray-900 transition gap-1 border border-gray-100 active:scale-95"
              title="Study Summary"
            >
              <i className="fa-solid fa-align-left text-gray-400 text-xs"></i>
              <span>Summary</span>
            </button>
          )}

          {showFlashcards && (
            <button
              type="button"
              onClick={(e) => handleAction(e, "flashcards")}
              className={`flex flex-col items-center justify-center py-2.5 rounded-xl text-[10px] font-bold transition gap-1 active:scale-95 shadow-xs ${theme.btn}`}
              title="Practice Flashcards"
            >
              <i className="fa-solid fa-layer-group text-xs"></i>
              <span>Cards</span>
            </button>
          )}

          {showQuiz && (
            <button
              type="button"
              onClick={(e) => handleAction(e, "quiz")}
              className={`flex flex-col items-center justify-center py-2.5 rounded-xl text-[10px] font-bold transition gap-1 active:scale-95 shadow-xs ${theme.btn}`}
              title="Take Practice Quiz"
            >
              <i className="fa-solid fa-bolt text-xs"></i>
              <span>Quiz</span>
            </button>
          )}

          {!anyEnabled && (
            <button
              type="button"
              disabled
              className="col-span-3 flex items-center justify-center py-2.5 rounded-xl text-[10px] font-bold text-gray-400 bg-gray-50 border border-gray-100 cursor-not-allowed gap-1.5"
            >
              <i className="fa-solid fa-ban text-xs"></i>
              <span>No Study Modes Active</span>
            </button>
          )}
        </div>
      </div>
    </article>
  );
};

export default NoteCard;
